import React, { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { Panel, SectionTitle, TimeAgo, Kpi } from "@/components/sov";
import { ScrollText, ShieldCheck, FileCheck2, AlertCircle, CheckCircle2, XCircle } from "lucide-react";

const ComplianceCard = ({ fw }) => {
  const score = fw.score;
  const color = score >= 95 ? "#6cc88f" : score >= 85 ? "#dcb95f" : score >= 70 ? "#f5a73a" : "#ff7a6e";
  const radius = 38;
  const circ = 2 * Math.PI * radius;
  const dash = (score / 100) * circ;

  return (
    <div className="sov-panel sov-corners p-5" data-testid={`compliance-${fw.framework.replace(/[^a-z0-9]+/gi, "-")}`}>
      <span className="corner tl" /><span className="corner tr" />
      <span className="corner bl" /><span className="corner br" />
      <div className="flex items-start gap-4">
        <div className="relative w-[100px] h-[100px] shrink-0">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            <circle cx="50" cy="50" r={radius} fill="none" stroke="rgba(200,162,75,0.12)" strokeWidth="6" />
            <circle
              cx="50" cy="50" r={radius}
              fill="none"
              stroke={color}
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={`${dash} ${circ}`}
              style={{ filter: `drop-shadow(0 0 6px ${color})`, transition: "stroke-dasharray 600ms ease" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="font-display text-[22px] text-gold-100" style={{ textShadow: `0 0 16px ${color}55` }}>
              {score}
            </div>
            <div className="font-mono text-[8px] text-ivory-3 tracking-[0.18em]">/100</div>
          </div>
        </div>
        <div className="flex-1">
          <div className="font-display text-[14px] tracking-[0.12em] text-gold-100 uppercase">
            {fw.framework}
          </div>
          <div className="font-mono text-[10px] text-ivory-3 mt-1.5 tracking-[0.1em]">
            {fw.controls_passing} / {fw.controls_total} controls passing
          </div>
          <div className="h-1 w-full bg-[var(--obsidian-3)] border border-[color:var(--gold-line)] mt-2">
            <div
              className="h-full"
              style={{
                width: `${(fw.controls_passing / fw.controls_total) * 100}%`,
                background: color,
              }}
            />
          </div>
          <div className="font-mono text-[9px] text-ivory-3 mt-2 tracking-[0.14em] uppercase">
            assessed <TimeAgo iso={fw.last_assessed} />
          </div>
        </div>
      </div>
    </div>
  );
};

const ResultBadge = ({ result }) => {
  const config = {
    success: { color: "#6cc88f", icon: CheckCircle2, label: "Success" },
    denied:  { color: "#ff7a6e", icon: XCircle, label: "Denied" },
  }[result] || { color: "#9bb1c8", icon: AlertCircle, label: result };
  const Icon = config.icon;
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.14em] uppercase" style={{ color: config.color }}>
      <Icon size={11} />
      {config.label}
    </span>
  );
};

const AuditGovernance = () => {
  const [logs, setLogs] = useState([]);
  const [compliance, setCompliance] = useState({ frameworks: [], overall_score: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [l, c] = await Promise.all([
          api.get("/audit/logs?limit=80"),
          api.get("/governance/compliance"),
        ]);
        setLogs(l.data.logs);
        setCompliance(c.data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = filter === "all" ? logs : logs.filter((l) => l.result === filter);

  const denied = logs.filter((l) => l.result === "denied").length;
  const success = logs.filter((l) => l.result === "success").length;

  if (loading) {
    return <div className="text-center py-20"><span className="dot-loader"><span /><span /><span /></span></div>;
  }

  return (
    <div className="space-y-6" data-testid="audit-governance">
      <SectionTitle sub="// CONTROL POSTURE & IMMUTABLE LEDGER">
        Audit & Governance
      </SectionTitle>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <Kpi
          testid="kpi-overall-posture"
          label="Overall Posture"
          value={`${compliance.overall_score}/100`}
          sub={compliance.overall_score >= 90 ? "⚜ sovereign-grade" : "⚜ vigilance required"}
          accent
        />
        <Kpi
          testid="kpi-frameworks"
          label="Frameworks Tracked"
          value={compliance.frameworks.length}
          sub="continuous attestation"
        />
        <Kpi
          testid="kpi-audit-success"
          label="Audit Events ✓"
          value={success}
          sub="last 7 days"
        />
        <Kpi
          testid="kpi-audit-denied"
          label="Audit Events ⨯"
          value={denied}
          sub="denied / forbidden"
        />
      </div>

      <Panel
        testid="panel-compliance"
        title="Compliance Posture"
        code="§ V.A"
        action={
          <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-ivory-3 flex items-center gap-2">
            <ShieldCheck size={11} className="text-gold-300" />
            continuous monitoring
          </span>
        }
      >
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="compliance-grid">
          {compliance.frameworks.map((fw) => (
            <ComplianceCard key={fw.framework} fw={fw} />
          ))}
        </div>
      </Panel>

      <Panel
        testid="panel-audit-log"
        title="Immutable Audit Ledger"
        code="§ V.B"
        action={
          <div className="flex items-center gap-2">
            {["all", "success", "denied"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`btn-ghost ${filter === f ? "ring-1 ring-[color:var(--gold-300)] text-gold-100" : ""}`}
                data-testid={`audit-filter-${f}`}
              >
                {f}
              </button>
            ))}
          </div>
        }
      >
        <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
          <table className="sov-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Target</th>
                <th>IP</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} data-testid={`audit-row-${l.id.slice(0, 6)}`}>
                  <td><TimeAgo iso={l.timestamp} /></td>
                  <td className="text-gold-100">{l.actor}</td>
                  <td className="text-ivory">{l.action}</td>
                  <td className="text-ivory-2">{l.target}</td>
                  <td className="text-ivory-3">{l.ip}</td>
                  <td><ResultBadge result={l.result} /></td>
                </tr>
              ))}
              {!filtered.length && (
                <tr><td colSpan={6} className="text-center py-8 text-ivory-3">No entries match filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
};

export default AuditGovernance;
